import type { Breadcrumb, Event } from '@sentry/core';

// Ephemeral login links (/link?token=..., /link/<token>) and any other `*token` query param must
// never reach Sentry. Keep in sync with the Segment source middleware in apps/sorare/index.html.
const redactQuery = (value: string): string =>
  value.replace(/((?:^|[?&])[^=&#]*token[^=&#]*=)[^&#]*/gi, '$1[REDACTED]');

// Full URLs, paths and referrers: a param only starts after `?` or `&`, so a `token` in a path
// segment or before the first `=` of an ordinary URL is left alone.
const redactUrl = (value: string): string =>
  value
    .replace(/\/link\/[^/?#]+/, '/link/[REDACTED]')
    .replace(/([?&][^=&#]*token[^=&#]*=)[^&#]*/gi, '$1[REDACTED]');

const redactFields = (
  target: Record<string, unknown> | undefined,
  fields: string[],
  redact: (value: string) => string
) => {
  if (!target) return;
  fields.forEach(field => {
    const value = target[field];
    if (typeof value === 'string') {
      // oxlint-disable-next-line no-param-reassign
      target[field] = redact(value);
    }
  });
};

const URL_DATA_FIELDS = ['url', 'from', 'to', 'http.url', 'url.full'];
// Bare query strings have no leading `?` (`request.query_string`, `url.query`) or may have one
// (`http.query`), so a token can sit at the very start.
const QUERY_DATA_FIELDS = ['http.query', 'url.query', 'query'];

const redactData = (data: Record<string, unknown> | undefined) => {
  redactFields(data, URL_DATA_FIELDS, redactUrl);
  redactFields(data, QUERY_DATA_FIELDS, redactQuery);
};

export const redactBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  redactData(breadcrumb.data);
  return breadcrumb;
};

export const redactEvent = <T extends Event>(event: T): T => {
  const { request, breadcrumbs, spans, contexts } = event;
  const requestFields = request as Record<string, unknown> | undefined;
  redactFields(requestFields, ['url'], redactUrl);
  redactFields(requestFields, ['query_string'], redactQuery);
  redactFields(request?.headers, ['Referer', 'referer'], redactUrl);
  breadcrumbs?.forEach(redactBreadcrumb);
  spans?.forEach(span => redactData(span.data));
  redactData(contexts?.trace?.data);
  return event;
};
